/**
 * Optimistic mutations and the three races (spec M0-F5 §5.3, acceptance group **O**).
 *
 * The hard part is not painting early — it is that **the Redis relay is frequently faster
 * than the HTTP response**, so all three of these happen in production and each needs a
 * different answer:
 *
 * | race | answer |
 * |---|---|
 * | the event arrives **after** the response | ordinary: settle, drop the inverse, keep the server's values |
 * | the event arrives **before** the response | record the id as confirmed and apply the server patch now; when the response lands it finds the id already confirmed and reconciles only the server-assigned fields (O2) |
 * | the response errors or times out | apply the inverse and toast — **and** keep listening: if an event with that id arrives afterwards the write did succeed and the response was lost, so re-apply and withdraw the toast (O4) |
 *
 * A patch that never settles within 10 s is rolled back on its own (O5), because a request
 * that neither resolves nor rejects is the one failure mode a promise cannot tell you about.
 *
 * This owns the bookkeeping only. What "apply" and "invert" mean is the store's business.
 */

/** How long an unsettled optimistic patch is kept before it is rolled back (§5.3 rule 5). */
export const OPTIMISTIC_TIMEOUT_MS = 10_000;
/** How long a `mutationId` confirmed by an early event is remembered (§5.3 race 2). */
export const CONFIRMED_TTL_MS = 60_000;

export interface PendingMutation<TInverse = unknown> {
  mutationId: string;
  /** What to apply to undo the optimistic patch. */
  inverse: TInverse;
  startedAt: number;
}

export interface OptimisticCallbacks<TInverse = unknown> {
  /** Undo the optimistic patch. */
  rollback(inverse: TInverse): void;
  /** Tell the user, and hand back a way to withdraw it if the write turns out to have won. */
  onError(mutationId: string): void;
  /** The error toast was wrong: the write did land (O4). */
  withdrawError(mutationId: string): void;
  now?(): number;
}

/**
 * One per board store. Not a hook: the socket delivers events outside React, and a class with
 * three maps is far easier to assert against than a reducer threaded through a provider.
 */
export class OptimisticTracker<TInverse = unknown> {
  private readonly pending = new Map<string, PendingMutation<TInverse>>();
  /** Ids whose event arrived before the response, with the time they arrived. */
  private readonly confirmed = new Map<string, number>();
  /** Ids whose request failed, kept so a late event can withdraw the toast (O4). */
  private readonly failed = new Set<string>();

  constructor(private readonly callbacks: OptimisticCallbacks<TInverse>) {}

  private now(): number {
    return this.callbacks.now?.() ?? Date.now();
  }

  /** Called when the optimistic patch has been painted and the request is on its way. */
  begin(mutationId: string, inverse: TInverse): void {
    this.pending.set(mutationId, { mutationId, inverse, startedAt: this.now() });
  }

  /** Is this id one of ours, still in flight? The reducer asks before applying an event. */
  isPending(mutationId: string): boolean {
    return this.pending.has(mutationId);
  }

  /**
   * An event carrying `mutationId` arrived.
   *
   * Returns whether the caller should **apply** the server patch. It should not when the
   * patch is still pending locally — the store already painted it, and re-applying is the
   * flicker (O1). It should when the id is unknown here, which is either another client's
   * write or our own after a failure (O4).
   */
  onEvent(mutationId: string): { apply: boolean } {
    if (this.failed.has(mutationId)) {
      // The write landed after all; the response was lost, not the write.
      this.failed.delete(mutationId);
      this.callbacks.withdrawError(mutationId);
      return { apply: true };
    }
    if (this.pending.has(mutationId)) {
      // Race 2: the event beat the response. Remember it so the response is a no-op.
      this.confirmed.set(mutationId, this.now());
      this.pending.delete(mutationId);
      return { apply: false };
    }
    return { apply: true };
  }

  /**
   * The tRPC response resolved. `true` when the event had already confirmed it, so the caller
   * reconciles only server-assigned fields (`id`, `position`, `updatedAt`) rather than
   * applying the whole answer a second time (O2).
   */
  settle(mutationId: string): { alreadyConfirmed: boolean } {
    const alreadyConfirmed = this.confirmed.delete(mutationId);
    this.pending.delete(mutationId);
    this.failed.delete(mutationId);
    return { alreadyConfirmed };
  }

  /** The tRPC response rejected: undo exactly what was painted, and nothing else (O3). */
  fail(mutationId: string): void {
    const entry = this.pending.get(mutationId);
    this.pending.delete(mutationId);
    if (!entry) return;
    this.callbacks.rollback(entry.inverse);
    // Kept, because the write may still have landed and the response been lost (O4).
    this.failed.add(mutationId);
    this.callbacks.onError(mutationId);
  }

  /**
   * Roll back anything that has been pending too long, and forget stale confirmations.
   * Driven by the store's own timer rather than one per mutation: one interval is cheaper
   * than N timeouts and the resolution this needs is seconds (O5).
   */
  sweep(): string[] {
    const now = this.now();
    const expired: string[] = [];
    for (const [mutationId, entry] of this.pending) {
      if (now - entry.startedAt < OPTIMISTIC_TIMEOUT_MS) continue;
      expired.push(mutationId);
      this.pending.delete(mutationId);
      this.callbacks.rollback(entry.inverse);
      this.callbacks.onError(mutationId);
      this.failed.add(mutationId);
    }
    for (const [mutationId, at] of this.confirmed) {
      if (now - at >= CONFIRMED_TTL_MS) this.confirmed.delete(mutationId);
    }
    return expired;
  }

  get size(): number {
    return this.pending.size;
  }

  clear(): void {
    this.pending.clear();
    this.confirmed.clear();
    this.failed.clear();
  }
}

/**
 * A fresh mutation id. `crypto.randomUUID` needs a secure context, which a board page always
 * is, but a jsdom test may not be — hence the fallback, which only has to be unique within
 * one browsing session.
 */
export function newMutationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Temporary keys for optimistically created rows (§5.3 rule 6). The server assigns the real
 * id; until it does the row needs *some* key, and it must be one no server id can collide
 * with — `tmp:` in front of a uuid is both.
 */
export function temporaryItemId(uuid = newMutationId()): string {
  return `tmp:${uuid}`;
}

export function isTemporaryId(id: string): boolean {
  return id.startsWith('tmp:');
}
